import { useMemo, useState } from 'react'
import { useMutation } from 'urql'
import { Button } from 'app/components/ui/button'
import { Checkbox } from 'app/components/ui/checkbox'
import { Label } from 'app/components/ui/label'
import type { AppointmentTypeJson, AvailabilityType, ProviderType } from 'app/types/entities'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { CREATE_AVAILABILITY_MUTATION, UPDATE_AVAILABILITY_MUTATION } from './CreateAvailabilityModal'
import { planStandardModeTypeUpdates } from 'app/lib/standardModeTypes'
import { OPEN_HOURS_DAYS } from './availabilityHours.utils'

// Standard mode ignores an availability's hours, but the API needs every day present (an empty week fails).
const CLOSED_WEEK = Object.fromEntries(OPEN_HOURS_DAYS.map((day) => [day, { open: false, hours: [] }]))

/**
 * In Standard mode the PMS supplies a provider's hours and chairs, so this panel only edits which
 * appointment types the provider can book.
 */
export function StandardModeProviderTypes({
  provider,
  appointmentTypes,
  availabilities,
  onSaved,
}: {
  provider: ProviderType
  appointmentTypes?: AppointmentTypeJson[]
  availabilities: AvailabilityType[]
  onSaved: () => void
}) {
  // Only types turned on in the Appointment Types tab are offered; saving leaves any others on the records untouched.
  const enabledTypes = useMemo(
    () => (appointmentTypes ?? []).filter((type) => type.activeScheduler),
    [appointmentTypes],
  )
  const bookableTypeIds = useMemo(() => enabledTypes.map((type) => type.vendorId!), [enabledTypes])
  const initialTypeIds = useMemo(
    () =>
      bookableTypeIds.filter((typeId) =>
        availabilities.some((availability) => availability.appointmentTypeIds?.includes(typeId)),
      ),
    [availabilities, bookableTypeIds],
  )
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[] | undefined>()
  const [isSaving, setIsSaving] = useState(false)
  const [, executeCreateAvailability] = useMutation(CREATE_AVAILABILITY_MUTATION)
  const [, executeUpdateAvailability] = useMutation(UPDATE_AVAILABILITY_MUTATION)

  const currentTypeIds = selectedTypeIds ?? initialTypeIds
  const isDirty =
    currentTypeIds.length !== initialTypeIds.length || currentTypeIds.some((id) => !initialTypeIds.includes(id))

  const toggleType = (typeId: string, checked: boolean) =>
    setSelectedTypeIds(checked ? [...currentTypeIds, typeId] : currentTypeIds.filter((id) => id !== typeId))

  const handleSave = async () => {
    setIsSaving(true)
    const results = availabilities.length
      ? await Promise.all(
          planStandardModeTypeUpdates(
            availabilities.map((availability) => ({
              id: availability.id!,
              appointmentTypeIds: availability.appointmentTypeIds ?? [],
            })),
            initialTypeIds,
            currentTypeIds,
          ).map((update) =>
            executeUpdateAvailability({ id: update.id, input: { appointmentTypeIds: update.appointmentTypeIds } }),
          ),
        )
      : // A provider with no availability yet gets one that carries only appointment types.
        [
          await executeCreateAvailability({
            input: {
              providerId: provider.id!,
              operatoryIds: [],
              appointmentTypeIds: currentTypeIds,
              timeSlots: CLOSED_WEEK,
            },
          }),
        ]
    setIsSaving(false)

    const failed = results.find((result) => result.error)
    if (failed) {
      notifyError(`Failed to save appointment types: ${failed.error!.message}`)
      return
    }
    notifySuccess(`Appointment types saved for ${provider.displayName}`)
    setSelectedTypeIds(undefined)
    onSaved()
  }

  if (!enabledTypes.length) {
    return (
      <p className="self-stretch border-t border-default px-4 py-3 text-body-sm text-subtle">
        Turn on at least one appointment type in the Appointment Types tab to make this provider bookable.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3 self-stretch border-t border-default px-4 py-3">
      <div className="flex flex-wrap gap-2">
        {enabledTypes.map((type) => {
          const checkboxId = `standard-type-${provider.id}-${type.vendorId}`
          return (
            <div
              key={type.vendorId}
              className="flex items-center gap-2 rounded-md border border-default px-2.5 py-1.5 hover:bg-surface-subtle"
            >
              <Checkbox
                id={checkboxId}
                checked={currentTypeIds.includes(type.vendorId!)}
                onCheckedChange={(checked) => toggleType(type.vendorId!, checked === true)}
              />
              <Label htmlFor={checkboxId} className="cursor-pointer">
                {type.displayName || type.name}
              </Label>
            </div>
          )
        })}
      </div>
      {!currentTypeIds.length && (
        <p className="text-body-sm text-subtle">
          With no appointment types, this provider can't be booked. To stop booking them, turn the provider off instead.
        </p>
      )}
      {isDirty && (
        <div className="flex gap-2">
          <Button size="sm" variant="primary" disabled={isSaving || !currentTypeIds.length} onClick={handleSave}>
            {isSaving ? `Saving…` : `Save`}
          </Button>
          <Button size="sm" variant="tertiary" disabled={isSaving} onClick={() => setSelectedTypeIds(undefined)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}
